import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Search, DollarSign, TrendingUp, AlertCircle, FileText, Settings, Edit2, CheckCircle, Eye, Trash2, Download, Plus, X, CheckSquare, Square, UserPlus, Camera, Image } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoiceManagement = ({ token, clients, onNavigate, navigationParams }) => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [costConfig, setCostConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCostConfig, setShowCostConfig] = useState(false);
  const [warehouseReceipts, setWarehouseReceipts] = useState([]);
  
  // Multi-select state
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    dateType: 'creacion',
    dateFrom: '',
    dateTo: '',
    status: 'todos'
  });

  useEffect(() => {
    if (navigationParams?.searchWr) {
      setFilters(prev => ({ ...prev, search: navigationParams.searchWr }));
    }
    if (navigationParams?.filterClientId) {
      const client = clients?.find(c => c.id === navigationParams.filterClientId);
      if (client) setFilters(prev => ({ ...prev, search: client.full_name }));
    }
  }, [navigationParams, clients]);
  
  // Cost configuration form
  const [costForm, setCostForm] = useState({
    cost_per_lb: 3.0,
    cost_per_ft3: 20.0,
    cost_repack: 3.0
  });

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const handleUploadComprobante = async (invoiceId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,application/pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/invoices/${invoiceId}/payment-proof`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        alert('Comprobante subido exitosamente');
      } catch (err) {
        alert('Error al subir comprobante: ' + (err.response?.data?.detail || err.message));
      }
    };
    input.click();
  };

  const handleViewComprobantes = async (invoiceId) => {
    try {
      const res = await axios.get(`${API}/invoices/${invoiceId}/payment-proof`, axiosConfig);
      if (res.data.length === 0) {
        alert('No hay comprobantes para esta factura');
        return;
      }
      // Open first comprobante in new tab via blob
      const file = res.data[0];
      const fileRes = await axios.get(`${API}/files/${file.storage_path}`, {
        ...axiosConfig, responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(fileRes.data);
      window.open(blobUrl, '_blank');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchFinancialSummary();
    fetchCostConfig();
    fetchWarehouseReceipts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [invoices, filters]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/invoices`, axiosConfig);
      setInvoices(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/financial-summary`, axiosConfig);
      setFinancialSummary(response.data);
    } catch (error) {
    }
  };

  const fetchCostConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/costs`, axiosConfig);
      setCostConfig(response.data);
      setCostForm({
        cost_per_lb: response.data.cost_per_lb,
        cost_per_ft3: response.data.cost_per_ft3,
        cost_repack: response.data.cost_repack
      });
    } catch (error) {
    }
  };

  const fetchWarehouseReceipts = async () => {
    try {
      const response = await axios.get(`${API}/admin/warehouse-receipts`, axiosConfig);
      // Filter only WRs that are not yet invoiced
      const uninvoicedWRs = response.data.filter(wr => wr.status !== 'invoiced');
      setWarehouseReceipts(uninvoicedWRs);
    } catch (error) {
    }
  };

  const applyFilters = () => {
    let filtered = [...invoices];
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(inv => 
        inv.invoice_number?.toLowerCase().includes(searchLower) ||
        getClientName(inv.client_id, inv).toLowerCase().includes(searchLower) ||
        inv.guias?.some(g => g?.toLowerCase().includes(searchLower))
      );
    }
    
    // Status filter
    if (filters.status !== 'todos') {
      filtered = filtered.filter(inv => inv.status === filters.status);
    }
    
    // Date filter
    if (filters.dateFrom || filters.dateTo) {
      filtered = filtered.filter(inv => {
        const dateField = filters.dateType === 'creacion' ? inv.created_at : inv.due_date;
        if (!dateField) return true;
        const invDate = new Date(dateField);
        if (filters.dateFrom && invDate < new Date(filters.dateFrom)) return false;
        if (filters.dateTo && invDate > new Date(filters.dateTo + 'T23:59:59')) return false;
        return true;
      });
    }
    
    setFilteredInvoices(filtered);
  };

  const handleUpdateCostConfig = async () => {
    try {
      await axios.put(`${API}/admin/costs`, costForm, axiosConfig);
      alert('Configuración actualizada exitosamente');
      fetchCostConfig();
      setShowCostConfig(false);
    } catch (error) {
      alert('Error al actualizar configuración');
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    if (!window.confirm('¿Marcar esta factura como pagada?')) return;
    
    try {
      await axios.put(`${API}/admin/invoices/${invoiceId}`, 
        { status: 'paid', paid_amount: invoices.find(i => i.id === invoiceId)?.total || 0 }, 
        axiosConfig
      );
      fetchInvoices();
      fetchFinancialSummary();
    } catch (error) {
      alert('Error al actualizar factura');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('¿Está seguro de eliminar esta factura? Esta acción no se puede deshacer.')) return;
    
    try {
      await axios.delete(`${API}/admin/invoices/${invoiceId}`, axiosConfig);
      fetchInvoices();
      fetchFinancialSummary();
      setSelectedInvoices(prev => prev.filter(id => id !== invoiceId));
      alert('Factura eliminada exitosamente');
    } catch (error) {
      alert('Error al eliminar factura: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Multi-select functions
  const toggleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedInvoices.length === filteredInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredInvoices.map(inv => inv.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedInvoices.length === 0) {
      alert('Seleccione al menos una factura para eliminar');
      return;
    }
    
    if (!window.confirm(`¿Está seguro de eliminar ${selectedInvoices.length} factura(s)? Esta acción no se puede deshacer.`)) return;
    
    try {
      // Delete invoices one by one
      for (const invoiceId of selectedInvoices) {
        await axios.delete(`${API}/admin/invoices/${invoiceId}`, axiosConfig);
      }
      
      setSelectedInvoices([]);
      fetchInvoices();
      fetchFinancialSummary();
      alert(`${selectedInvoices.length} factura(s) eliminada(s) exitosamente`);
    } catch (error) {
      alert('Error al eliminar facturas: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBulkMarkAsPaid = async () => {
    if (selectedInvoices.length === 0) {
      alert('Seleccione al menos una factura');
      return;
    }
    
    if (!window.confirm(`¿Marcar ${selectedInvoices.length} factura(s) como pagada(s)?`)) return;
    
    try {
      for (const invoiceId of selectedInvoices) {
        const invoice = invoices.find(i => i.id === invoiceId);
        if (invoice && invoice.status !== 'paid') {
          await axios.put(`${API}/admin/invoices/${invoiceId}`, 
            { status: 'paid', paid_amount: invoice.total || 0 }, 
            axiosConfig
          );
        }
      }
      
      setSelectedInvoices([]);
      fetchInvoices();
      fetchFinancialSummary();
      alert('Facturas actualizadas exitosamente');
    } catch (error) {
      alert('Error al actualizar facturas');
    }
  };

  const openEditModal = (invoice) => {
    setSelectedInvoice(invoice);
    setEditForm({
      invoice_number: invoice.invoice_number || '',
      status: invoice.status,
      paid_amount: invoice.paid_amount || 0,
      due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
      comments: invoice.comments || '',
      terms: invoice.terms || '',
      // Campos para facturas manuales
      client_name: invoice.client_name || '',
      client_email: invoice.client_email || '',
      client_address: invoice.client_address || '',
      client_id_document: invoice.client_id_document || ''
    });
    setShowEditModal(true);
  };

  const openViewModal = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedInvoice) return;
    
    setSaving(true);
    try {
      const updateData = {
        status: editForm.status,
        paid_amount: parseFloat(editForm.paid_amount) || 0,
        due_date: editForm.due_date || null,
        comments: editForm.comments,
        terms: editForm.terms
      };
      
      // Include invoice number if changed
      if (editForm.invoice_number && editForm.invoice_number !== selectedInvoice.invoice_number) {
        updateData.invoice_number = editForm.invoice_number;
      }
      
      // Include client data for manual invoices
      if (selectedInvoice.client_id === 'manual') {
        updateData.client_name = editForm.client_name;
        updateData.client_email = editForm.client_email;
        updateData.client_address = editForm.client_address;
        updateData.client_id_document = editForm.client_id_document;
      }
      
      await axios.put(`${API}/admin/invoices/${selectedInvoice.id}`, updateData, axiosConfig);
      
      alert('Factura actualizada exitosamente');
      setShowEditModal(false);
      fetchInvoices();
      fetchFinancialSummary();
    } catch (error) {
      alert('Error al actualizar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await axios.get(
        `${API}/admin/invoices/${invoice.id}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FACTURA_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error al descargar PDF');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      paid: 'bg-green-100 text-green-800 border-green-300',
      overdue: 'bg-red-100 text-red-800 border-red-300',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-300',
      partial: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'PENDIENTE',
      paid: 'COBRADA',
      overdue: 'VENCIDA',
      cancelled: 'ANULADA',
      partial: 'PARCIAL'
    };
    return labels[status] || status?.toUpperCase();
  };

  const getClientName = (clientId, invoice = null) => {
    // For manual invoices, use the client_name stored in the invoice
    if (clientId === 'manual' && invoice?.client_name) {
      return invoice.client_name;
    }
    if (clientId === 'manual') return 'Cliente Manual';
    const client = clients?.find(c => c.id === clientId);
    return client?.full_name || 'Cliente desconocido';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate due date (default 15 days from creation)
  const getDueDate = (invoice) => {
    if (invoice.due_date) return formatDate(invoice.due_date);
    const created = new Date(invoice.created_at);
    created.setDate(created.getDate() + (invoice.payment_term_days || 15));
    return formatDate(created.toISOString());
  };

  // Recalculate all invoices
  const handleRecalculateAll = async () => {
    if (!window.confirm('¿Recalcular costos y ganancias de todas las facturas basado en la configuración actual?')) return;
    
    try {
      const response = await axios.post(`${API}/admin/invoices/recalculate-all`, {}, axiosConfig);
      alert(`✅ ${response.data.message}\nCosto/lb: $${response.data.cost_per_lb}\nCosto/ft³: $${response.data.cost_per_ft3}`);
      fetchInvoices();
      fetchFinancialSummary();
    } catch (error) {
      alert('Error al recalcular: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Register manual client as frequent client
  const handleRegisterManualClient = async (invoice) => {
    if (!invoice.client_name) {
      alert('Esta factura no tiene nombre de cliente');
      return;
    }
    
    if (!window.confirm(`¿Registrar a "${invoice.client_name}" como cliente frecuente?\n\nEsto creará una cuenta con casillero y actualizará todas las facturas/WRs asociadas a este cliente.`)) return;
    
    try {
      const response = await axios.post(`${API}/admin/clients/from-manual`, {
        full_name: invoice.client_name,
        email: invoice.client_email,
        id_document: invoice.client_id_document,
        delivery_address: invoice.client_address,
        phone: ''
      }, axiosConfig);
      
      alert(`✅ ${response.data.message}\n\n📧 Email: ${response.data.email}\n🔑 Contraseña temporal: ${response.data.temp_password}\n📦 Casillero: ${response.data.locker_id}`);
      fetchInvoices();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="space-y-4 p-4">
      {/* Header with title and actions */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Facturas</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRecalculateAll}
            className="gap-2 text-purple-600 border-purple-300 hover:bg-purple-50"
            title="Recalcular ganancias de todas las facturas"
          >
            <TrendingUp size={16} />
            Recalcular Ganancias
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowCostConfig(!showCostConfig)}
            className="gap-2"
          >
            <Settings size={16} />
            Configurar Costos
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              // Export to Excel/CSV
              const csv = [
                ['Nº Factura', 'Cliente', 'F. Creación', 'F. Vencimiento', 'Total', 'Pagado', 'Balance', 'Status'],
                ...filteredInvoices.map(inv => [
                  inv.invoice_number,
                  getClientName(inv.client_id, inv),
                  formatDate(inv.created_at),
                  getDueDate(inv),
                  inv.total?.toFixed(2),
                  inv.paid_amount?.toFixed(2),
                  inv.balance_due?.toFixed(2),
                  getStatusLabel(inv.status)
                ])
              ].map(row => row.join(',')).join('\n');
              
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `facturas_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
            }}
          >
            <Download size={16} />
            Reportes
          </Button>
        </div>
      </div>

      {/* Cost Configuration Modal */}
      {showCostConfig && (
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Settings className="h-5 w-5" />
              Configuración de Costos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label>Costo por Libra ($)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={costForm.cost_per_lb}
                  onChange={(e) => setCostForm({ ...costForm, cost_per_lb: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Costo por Ft³ ($)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={costForm.cost_per_ft3}
                  onChange={(e) => setCostForm({ ...costForm, cost_per_ft3: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Costo de Reempaque ($)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={costForm.cost_repack}
                  onChange={(e) => setCostForm({ ...costForm, cost_repack: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCostConfig(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdateCostConfig}>
                Guardar Configuración
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Summary Cards */}
      {financialSummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <p className="text-xs font-medium opacity-90">Total Monto</p>
              <p className="text-xl font-bold">${financialSummary.total_amount?.toFixed(2) || '0.00'}</p>
              <p className="text-xs opacity-75">{financialSummary.total_invoices || 0} facturas</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <p className="text-xs font-medium opacity-90">Cobrado</p>
              <p className="text-xl font-bold">${financialSummary.total_collected?.toFixed(2) || '0.00'}</p>
              <p className="text-xs opacity-75">{financialSummary.paid_count || 0} pagadas</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
            <CardContent className="p-4">
              <p className="text-xs font-medium opacity-90">Pendiente</p>
              <p className="text-xl font-bold">${financialSummary.total_pending?.toFixed(2) || '0.00'}</p>
              <p className="text-xs opacity-75">{financialSummary.pending_count || 0} pendientes</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-4">
              <p className="text-xs font-medium opacity-90">Costo Total</p>
              <p className="text-xl font-bold">${financialSummary.total_cost?.toFixed(2) || '0.00'}</p>
              <p className="text-xs opacity-75">Costos op.</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <p className="text-xs font-medium opacity-90">Ganancia Est.</p>
              <p className="text-xl font-bold">${financialSummary.estimated_profit?.toFixed(2) || '0.00'}</p>
              <p className="text-xs opacity-75">Utilidad</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters Row - Similar to reference image */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-600">Buscar por número</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Nº de factura..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-8"
                />
              </div>
            </div>

            {/* Date Type */}
            <div className="w-[140px]">
              <Label className="text-xs text-gray-600">Tipo fecha</Label>
              <Select value={filters.dateType} onValueChange={(v) => setFilters({ ...filters, dateType: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="creacion">F. Creación</SelectItem>
                  <SelectItem value="vencimiento">F. Vencimiento</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div className="w-[140px]">
              <Label className="text-xs text-gray-600">Desde</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>

            {/* Date To */}
            <div className="w-[140px]">
              <Label className="text-xs text-gray-600">Hasta</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>

            {/* Status */}
            <div className="w-[140px]">
              <Label className="text-xs text-gray-600">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="paid">Cobrada</SelectItem>
                  <SelectItem value="overdue">Vencida</SelectItem>
                  <SelectItem value="partial">Parcial</SelectItem>
                  <SelectItem value="cancelled">Anulada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({
                search: '',
                dateType: 'creacion',
                dateFrom: '',
                dateTo: '',
                status: 'todos'
              })}
            >
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedInvoices.length > 0 && (
        <Card className="border-2 border-blue-300 bg-blue-50">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckSquare className="text-blue-600" size={20} />
                <span className="font-semibold text-blue-800">
                  {selectedInvoices.length} factura(s) seleccionada(s)
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkMarkAsPaid}
                  className="gap-1 text-green-600 border-green-300 hover:bg-green-50"
                >
                  <CheckCircle size={14} />
                  Marcar Pagadas
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkDelete}
                  className="gap-1 text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 size={14} />
                  Eliminar Seleccionadas
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedInvoices([])}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices Table */}
      <Card>
        <CardHeader className="py-3 px-4 border-b bg-gray-50">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base font-semibold">
              Facturas ({filteredInvoices.length})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando facturas...</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No se encontraron facturas
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-100">
                    <th className="px-2 py-2 text-center w-10">
                      <button
                        onClick={toggleSelectAll}
                        className="p-1 hover:bg-gray-200 rounded"
                        title={selectedInvoices.length === filteredInvoices.length ? "Deseleccionar todo" : "Seleccionar todo"}
                      >
                        {selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0 ? (
                          <CheckSquare size={16} className="text-blue-600" />
                        ) : (
                          <Square size={16} className="text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Nº Factura</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Cliente</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">F. Creación</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">F. Vencimiento</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Nro. WR</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Monto</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Pagado</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Balance</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-700">Status</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className={`border-b hover:bg-gray-50 ${selectedInvoices.includes(invoice.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={() => toggleSelectInvoice(invoice.id)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          {selectedInvoices.includes(invoice.id) ? (
                            <CheckSquare size={16} className="text-blue-600" />
                          ) : (
                            <Square size={16} className="text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-medium">
                        <button 
                          onClick={() => onNavigate && onNavigate('invoice-detail', { invoiceData: invoice })}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-semibold"
                        >
                          {invoice.invoice_number}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        <button onClick={() => onNavigate && onNavigate('clients', { filterClientId: invoice.client_id })} className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer">
                          {getClientName(invoice.client_id, invoice)}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {formatDate(invoice.created_at)}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {getDueDate(invoice)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {invoice.guias?.length > 0 ? (
                          <button onClick={() => onNavigate && onNavigate('warehouse', { searchWr: invoice.guias[0] })} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium hover:bg-blue-200 cursor-pointer">
                            {invoice.guias[0]}
                          </button>
                        ) : (
                          <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded text-xs">{invoice.wr_count || 1}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">
                        ${invoice.total?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-3 py-2 text-right text-green-600">
                        ${invoice.paid_amount?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-3 py-2 text-right text-orange-600 font-medium">
                        ${invoice.balance_due?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${getStatusColor(invoice.status)}`}>
                          {getStatusLabel(invoice.status)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openViewModal(invoice)}
                            title="Ver detalle"
                            className="h-7 w-7 p-0"
                          >
                            <Eye size={14} className="text-blue-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditModal(invoice)}
                            title="Editar"
                            className="h-7 w-7 p-0"
                          >
                            <Edit2 size={14} className="text-yellow-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadPDF(invoice)}
                            title="Descargar PDF"
                            className="h-7 w-7 p-0"
                          >
                            <FileText size={14} className="text-gray-600" />
                          </Button>
                          {invoice.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMarkAsPaid(invoice.id)}
                              title="Marcar como pagada"
                              className="h-7 w-7 p-0"
                            >
                              <CheckCircle size={14} className="text-green-600" />
                            </Button>
                          )}
                          {invoice.client_id === 'manual' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRegisterManualClient(invoice)}
                              title="Registrar como cliente frecuente"
                              className="h-7 w-7 p-0"
                            >
                              <UserPlus size={14} className="text-purple-600" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUploadComprobante(invoice.id)}
                            title="Subir comprobante de pago"
                            className="h-7 w-7 p-0"
                            data-testid={`upload-proof-${invoice.id}`}
                          >
                            <Camera size={14} className="text-teal-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewComprobantes(invoice.id)}
                            title="Ver comprobantes"
                            className="h-7 w-7 p-0"
                            data-testid={`view-proof-${invoice.id}`}
                          >
                            <Image size={14} className="text-indigo-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            title="Eliminar"
                            className="h-7 w-7 p-0"
                          >
                            <Trash2 size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Detalle de Factura {selectedInvoice.invoice_number}</h2>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Cliente</p>
                  <p className="font-semibold">{getClientName(selectedInvoice.client_id, selectedInvoice)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Estado</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${getStatusColor(selectedInvoice.status)}`}>
                    {getStatusLabel(selectedInvoice.status)}
                  </span>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Fecha Creación</p>
                  <p className="font-semibold">{formatDate(selectedInvoice.created_at)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Fecha Vencimiento</p>
                  <p className="font-semibold">{getDueDate(selectedInvoice)}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Servicios</h3>
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-2 py-1 text-left">Descripción</th>
                      <th className="px-2 py-1 text-right">Cant.</th>
                      <th className="px-2 py-1 text-right">Precio</th>
                      <th className="px-2 py-1 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items?.map((item, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="px-2 py-1">{item.description}</td>
                        <td className="px-2 py-1 text-right">{item.quantity} {item.unit}</td>
                        <td className="px-2 py-1 text-right">${item.rate?.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right">${item.amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-1">
                    <div className="flex justify-between">
                      <span>Total:</span>
                      <span className="font-bold">${selectedInvoice.total?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Pagado:</span>
                      <span>${selectedInvoice.paid_amount?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-orange-600 font-semibold">
                      <span>Balance:</span>
                      <span>${selectedInvoice.balance_due?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {selectedInvoice.comments && (
                <div className="bg-yellow-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Comentarios</p>
                  <p>{selectedInvoice.comments}</p>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowViewModal(false)}>
                Cerrar
              </Button>
              <Button onClick={() => handleDownloadPDF(selectedInvoice)}>
                <FileText size={16} className="mr-2" />
                Descargar PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {showEditModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Editar Factura {selectedInvoice.invoice_number}</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Client data for manual invoices */}
              {selectedInvoice.client_id === 'manual' && (
                <div className="bg-yellow-50 p-3 rounded border border-yellow-200 space-y-3">
                  <p className="text-sm font-semibold text-yellow-800">📝 Datos del Cliente (Factura Manual)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Nombre Cliente</Label>
                      <Input
                        value={editForm.client_name}
                        onChange={(e) => setEditForm({...editForm, client_name: e.target.value})}
                        className="mt-1"
                        placeholder="Nombre completo"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input
                        type="email"
                        value={editForm.client_email}
                        onChange={(e) => setEditForm({...editForm, client_email: e.target.value})}
                        className="mt-1"
                        placeholder="correo@ejemplo.com"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Cédula/Doc. ID</Label>
                      <Input
                        value={editForm.client_id_document}
                        onChange={(e) => setEditForm({...editForm, client_id_document: e.target.value})}
                        className="mt-1"
                        placeholder="V-12345678"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Dirección</Label>
                      <Input
                        value={editForm.client_address}
                        onChange={(e) => setEditForm({...editForm, client_address: e.target.value})}
                        className="mt-1"
                        placeholder="Dirección de entrega"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Editable Invoice Number */}
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <Label className="text-sm font-semibold text-blue-800">📋 Número de Factura (Editable)</Label>
                <Input
                  value={editForm.invoice_number}
                  onChange={(e) => setEditForm({...editForm, invoice_number: e.target.value})}
                  className="mt-1"
                  placeholder="BILL17XXX"
                />
                <p className="text-xs text-blue-600 mt-1">
                  Puedes cambiar el número para sincronizar con tu sistema externo
                </p>
              </div>
              
              <div>
                <Label>Estado</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="paid">Cobrada</SelectItem>
                    <SelectItem value="partial">Parcial</SelectItem>
                    <SelectItem value="overdue">Vencida</SelectItem>
                    <SelectItem value="cancelled">Anulada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Monto Pagado ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.paid_amount}
                  onChange={(e) => setEditForm({...editForm, paid_amount: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Total factura: ${selectedInvoice.total?.toFixed(2)} | 
                  Balance actual: ${(selectedInvoice.total - (parseFloat(editForm.paid_amount) || 0)).toFixed(2)}
                </p>
              </div>
              
              <div>
                <Label>Fecha de Vencimiento</Label>
                <Input
                  type="date"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm({...editForm, due_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Comentarios</Label>
                <Textarea
                  value={editForm.comments}
                  onChange={(e) => setEditForm({...editForm, comments: e.target.value})}
                  placeholder="Notas o comentarios..."
                  rows={2}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Términos</Label>
                <Textarea
                  value={editForm.terms}
                  onChange={(e) => setEditForm({...editForm, terms: e.target.value})}
                  placeholder="Términos de pago..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceManagement;
